<script setup>
import { Head } from '@inertiajs/vue3'
import { ratio } from '@/helpers'

const breadcrumb = defineAsyncComponent(() => import('@components/Breadcrumb.vue'))
const simplePaginator = defineAsyncComponent(() => import('@components/SimplePaginator.vue'))

const props = defineProps({
	author: {
		type: Object,
		default: () => {},
	},
	paginator: {
		type: Object,
		default: () => {},
	},
	schema: {
		type: Object,
		default: () => {},
	},
})

const socials = computed(() => {
	return [
		{
			label: 'Instagram',
			value: props.author.instagram,
			icon: 'str-instagram-fill',
		},
		{
			label: 'Facebook',
			value: props.author.facebook,
			icon: 'str-facebook-fill',
		},
		{
			label: 'Twitter',
			value: props.author.twitter,
			icon: 'str-x-com',
		},
	].filter(s => {
		if (s.value) return s
	})
})

const canonical = computed(() => {
	const currentPageUrl = new URL(props.paginator.links.filter(page => page.active)[0].url)

	if (props.paginator.current_page === 1) {
		currentPageUrl.searchParams.delete('page')
	}

	return currentPageUrl.href
})

// Add SEO
const pageMeta = [
	{ tag: 'link', tagItem: 'rel', tagValue: 'canonical', contentItem: 'href', contentValue: canonical.value },
	// { tag: 'meta', tagItem: 'name', tagValue: 'title', contentItem: 'content', contentValue: `${capitalize(props.tag)} - Lekker` },
	// { tag: 'meta', tagItem: 'property', tagValue: 'og:title', contentItem: 'content', contentValue: `${capitalize(props.tag)} - Lekker` },
	// { tag: 'meta', tagItem: 'property', tagValue: 'og:url', contentItem: 'content', contentValue: canonical.value },
	// { tag: 'meta', tagItem: 'property', tagValue: 'twitter:title', contentItem: 'content', contentValue: `${capitalize(props.tag)} - Lekker` },
	// { tag: 'meta', tagItem: 'name', tagValue: 'twitter:card', contentItem: 'content', contentValue: 'summary' },
	// { tag: 'meta', tagItem: 'name', tagValue: 'twitter:site', contentItem: 'content', contentValue: '@lekker500' },
]
</script>

<template>
	<Head>
		<title>{{ author.first_name }} {{ author.last_name }}</title>
		<component
			:is="`${meta.tag}`"
			v-for="(meta, metaIndex) in pageMeta"
			:key="`pagemeta-${metaIndex}`"
			:[meta.tagItem]="meta.tagValue"
			:[meta.contentItem]="meta.contentValue"
		/>

		<component
			:is="`script`"
			type="application/ld+json"
		>
			{{ schema }}
		</component>
	</Head>
	<main class="page-main py-4">
		<section class="container-xxl main-wrapper mb-3 mb-lg-5">
			<div class="row">
				<div class="col-12 mb-3 mb-lg-4">
					<client-only>
						<breadcrumb :nav-items="[{ label: 'Home', route: '/' }]" />
					</client-only>
				</div>
				<div
					class="col-12 text-center mx-auto mb-4"
					style="max-width: 800px"
				>
					<img
						:src="
							$thumbor(author.avatar ? author.avatar : 'https://cdn.pijper.io/core/lekker-fallback.png')
								.dimension(100, 100)
								.filters('format(webp)')
								.url()
						"
						:alt="`${author.first_name} ${author.last_name}`"
						class="mb-3 rounded-circle border"
					/>

					<h1 class="mb-3 ff-serif fs-2 fw-boldest">{{ author.first_name }} {{ author.last_name }}</h1>

					<p
						v-if="author.bio"
						class="lead"
					>
						{{ author.bio }}
					</p>

					<ul
						v-if="socials.length"
						class="list-unstyled d-flex justify-content-center gap-2 fs-4"
					>
						<li
							v-for="(s, sIndex) in socials"
							:key="`social-${sIndex}`"
						>
							<a
								:href="s.value"
								class="text-reset"
								style="text-decoration: none"
							>
								<i :class="s.icon" />
							</a>
						</li>
					</ul>
					<!-- <pre>{{ author }}</pre> -->
				</div>

				<h2
					v-if="paginator.data.length"
					class="h4 text-center mb-4"
				>
					{{ $t('Recent articles') }}
				</h2>

				<div
					v-for="(article, articleIndex) in paginator.data"
					:key="article.id"
					:class="[article.type === 1 ? 'col-6 col-lg-3 mb-3 mb-lg-4' : 'col-6 col-md-3 col-xl-2 mb-3 mb-lg-4']"
				>
					<theme-card-plain
						v-if="article.type === 1"
						tag="article"
						:item="article"
						style="--card-aspect-ratio: 1 / 0.75"
						class="h-100"
					>
						<template #img="{ meta }">
							<thumbor-image
								:use="['sm', 'md']"
								:dimensions="{
									sm: [219, ratio(219)],
									md: [306, ratio(306)],
								}"
								:img="meta.imgSource"
								class="w-100 mb-0 bg-neutral-300"
								:classes="{
									img: 'custom-card-img image-fluid w-100',
								}"
								:loading="articleIndex <= 5 ? false : 'lazy'"
								:crop-preset="meta.imgCropping"
								filters="format(webp)"
							/>
						</template>
					</theme-card-plain>

					<theme-card-tip
						v-else-if="article.type === 2"
						tag="article"
						:item="article"
						style="--card-aspect-ratio: 1 / 0.75"
						class="h-100"
					>
						<template #img="{ meta }">
							<thumbor-image
								:use="['md']"
								:dimensions="{
									md: [350, ratio(350, 1, 0.7)],
								}"
								:img="meta.imgSource"
								class="w-100 mb-2"
								:classes="{
									img: 'custom-card-img image-fluid rounded-bottom bg-neutral-300 w-100',
								}"
								:crop-preset="meta.imgCropping"
								:img-alt="meta.imgAlt"
								filters="format(webp)"
							/>
						</template>
					</theme-card-tip>
				</div>

				<simple-paginator :paginator="paginator" />
			</div>
		</section>
	</main>
	<!-- <pre>{{ author }}</pre> -->
	<!-- <pre>{{ relatedArticles }}</pre> -->
</template>
